<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'

export interface BaseSliderProps {
  modelValue: boolean
  title?: string
  subtitle?: string
  width?: string | number
  position?: 'left' | 'right'
  persistent?: boolean
  loading?: boolean
  closeOnEscape?: boolean
}

const props = withDefaults(defineProps<BaseSliderProps>(), {
  title: '',
  subtitle: '',
  width: 480,
  position: 'right',
  persistent: false,
  loading: false,
  closeOnEscape: true
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
  (e: 'open'): void
}>()

// Internal state for animation control
const isVisible = ref(false) // Controls DOM presence
const isOpen = ref(false) // Panel is in open state
const isClosing = ref(false) // Panel is animating closed
const titleId = `slider-title-${Math.random().toString(36).substring(2, 9)}`

// Exit animation duration in ms (should match $duration-exit in the styles)
const EXIT_DURATION = 200

// Computed
const panelWidth = computed(() => {
  if (typeof props.width === 'number') {
    return `${props.width}px`
  }
  return props.width
})

const panelClasses = computed(() => ({
  'slider-panel': true,
  'position-left': props.position === 'left',
  'position-right': props.position === 'right',
  'is-open': isOpen.value,
  'is-closing': isClosing.value
}))

const overlayClasses = computed(() => ({
  'slider-overlay': true,
  'justify-start': props.position === 'left',
  'justify-end': props.position === 'right',
  'is-open': isOpen.value,
  'is-closing': isClosing.value
}))

// Methods
function handleClose() {
  if (props.loading) return
  if (isClosing.value) return // Already closing

  // Start close animation - keep isOpen true so transform stays at 0
  isClosing.value = true
  emit('update:modelValue', false)
  emit('close')

  // After animation completes, clean up
  setTimeout(() => {
    isOpen.value = false
    isClosing.value = false
    isVisible.value = false
  }, EXIT_DURATION)
}

function handleOverlayClick() {
  if (!props.persistent) {
    handleClose()
  }
}

// Keyboard handler
let keydownHandler: ((e: KeyboardEvent) => void) | null = null

function setupKeyboardHandler() {
  keydownHandler = (e: KeyboardEvent) => {
    if (props.closeOnEscape && e.key === 'Escape' && isVisible.value && !props.loading && !isClosing.value) {
      handleClose()
    }
  }
  document.addEventListener('keydown', keydownHandler)
}

function cleanupKeyboardHandler() {
  if (keydownHandler) {
    document.removeEventListener('keydown', keydownHandler)
    keydownHandler = null
  }
}

// Watchers
watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      // Opening: show immediately, then trigger animation
      isVisible.value = true
      isClosing.value = false
      nextTick(() => {
        // Small delay to ensure DOM is ready for animation
        requestAnimationFrame(() => {
          isOpen.value = true
        })
      })
      emit('open')
    } else if (isVisible.value && !isClosing.value) {
      // External close (not via handleClose) - trigger close animation
      handleClose()
    }
  },
  { immediate: true }
)

// Lifecycle
onMounted(() => {
  setupKeyboardHandler()
})

onUnmounted(() => {
  cleanupKeyboardHandler()
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      :class="overlayClasses"
      @click="handleOverlayClick"
    >
      <div
        :class="panelClasses"
        :style="{ width: panelWidth }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
        @click.stop
      >
        <!-- Header -->
        <div v-if="$slots.header || title" class="slider-header">
          <slot name="header">
            <div class="header-content">
              <h2 v-if="title" :id="titleId">{{ title }}</h2>
              <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
            </div>
            <button
              type="button"
              class="close-button"
              :disabled="loading"
              aria-label="Close"
              @click="handleClose"
            >
              &times;
            </button>
          </slot>
        </div>

        <!-- Content -->
        <div class="slider-content">
          <slot />
        </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="slider-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
// Animation timing - separate enter/exit for snappy close
$duration-enter: 280ms;
$duration-exit: 200ms;
$easing-enter: cubic-bezier(0.16, 1, 0.3, 1); // Smooth deceleration
$easing-exit: cubic-bezier(0.32, 0, 0.67, 0); // Quick acceleration out

.slider-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  /* Must stay below Vuetify's overlay floor (2000): select/combobox menus and
     dialogs opened from inside the slider render in .v-overlay-container at
     z-index 2000, and with an equal z-index the slider (later DOM sibling)
     paints over them — the dropdown opens but is invisible. */
  z-index: 1990;
  display: flex;
  opacity: 0;
  transition: opacity $duration-exit $easing-exit, backdrop-filter $duration-exit $easing-exit;

  &.is-open {
    opacity: 1;
    transition: opacity $duration-enter $easing-enter, backdrop-filter $duration-enter $easing-enter;

    // Closing overrides open - animate back out
    &.is-closing {
      opacity: 0;
      backdrop-filter: blur(0);
      transition: opacity $duration-exit $easing-exit, backdrop-filter $duration-exit $easing-exit;
    }
  }

  &.justify-start {
    justify-content: flex-start;
  }

  &.justify-end {
    justify-content: flex-end;
  }
}

.slider-panel {
  max-width: 100vw;
  height: 100%;
  background: rgb(15, 23, 42);
  display: flex;
  flex-direction: column;
  will-change: transform;

  &.position-right {
    transform: translateX(100%);
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.4);
    transition: transform $duration-exit $easing-exit;

    &.is-open {
      transform: translateX(0);
      transition: transform $duration-enter $easing-enter;

      // Closing overrides open - animate back out
      &.is-closing {
        transform: translateX(100%);
        transition: transform $duration-exit $easing-exit;
      }
    }
  }

  &.position-left {
    transform: translateX(-100%);
    box-shadow: 8px 0 32px rgba(0, 0, 0, 0.4);
    transition: transform $duration-exit $easing-exit;

    &.is-open {
      transform: translateX(0);
      transition: transform $duration-enter $easing-enter;

      // Closing overrides open - animate back out
      &.is-closing {
        transform: translateX(-100%);
        transition: transform $duration-exit $easing-exit;
      }
    }
  }
}

.slider-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  .header-content {
    flex: 1;
    min-width: 0;
  }

  h2 {
    margin: 0;
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.15rem;
    font-weight: 600;
  }

  .subtitle {
    margin: 4px 0 0;
    color: rgba(200, 218, 255, 0.5);
    font-size: 0.85rem;
  }

  .close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    background: rgba(120, 163, 255, 0.08);
    border: 1px solid rgba(120, 163, 255, 0.1);
    border-radius: 10px;
    color: rgba(200, 218, 255, 0.6);
    font-size: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: rgba(244, 67, 54, 0.15);
      border-color: rgba(244, 67, 54, 0.2);
      color: #ef5350;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

// Content sections with staggered fade-in on open
.slider-header,
.slider-content,
.slider-footer {
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 150ms $easing-exit, transform 150ms $easing-exit;
}

.is-open {
  .slider-header {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 60ms;
    transition-duration: 200ms;
    transition-timing-function: $easing-enter;
  }

  .slider-content {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 100ms;
    transition-duration: 200ms;
    transition-timing-function: $easing-enter;
  }

  .slider-footer {
    opacity: 1;
    transform: translateY(0);
    transition-delay: 140ms;
    transition-duration: 200ms;
    transition-timing-function: $easing-enter;
  }

  // Quick fade out on close - all sections fade together
  &.is-closing {
    .slider-header,
    .slider-content,
    .slider-footer {
      opacity: 0;
      transform: translateY(-4px);
      transition: opacity 80ms $easing-exit, transform 80ms $easing-exit;
      transition-delay: 0ms;
    }
  }
}

.slider-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.slider-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

// Respect reduced motion preference
@media (prefers-reduced-motion: reduce) {
  .slider-overlay,
  .slider-panel,
  .slider-header,
  .slider-content,
  .slider-footer {
    transition: none !important;
    animation: none !important;
  }

  .slider-overlay {
    opacity: 1;
  }

  .slider-panel.position-right,
  .slider-panel.position-left {
    transform: translateX(0);
  }

  .slider-header,
  .slider-content,
  .slider-footer {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>
